import React, { useRef } from 'react';
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { BadgeCheckIcon, ReceiptTextIcon } from 'lucide-react';
import { OccasionSearch } from './OccasionSearch';
import { IMG } from '../../data/images';
import { useLocale } from '../../contexts/LocaleContext';
import { useIsDesktop } from '../../hooks/useIsDesktop';
import { spring, staggerContainer } from '../../utils/motion';
export type HeroTreatment = 'editorial' | 'immersive';
function Headline({
  text,
  className
}: {
  text: string;
  className?: string;
}) {
  const reduce = useReducedMotion();
  const words = text.split(' ');
  return <motion.h1 variants={staggerContainer(0.04, 0.05)} initial="hidden" animate="show" className={className} aria-label={text} data-id="element-506" data-mp="qm9s7vrng4x">
      {words.map((word, i) => <span key={`${word}-${i}`} className="inline-block overflow-hidden pb-1 align-bottom" data-id="element-507" data-mp="1v3ltod76iwi0" data-mpi={i}>
          <motion.span variants={reduce ? {
        hidden: {
          opacity: 0
        },
        show: {
          opacity: 1,
          transition: {
            duration: 0.15
          }
        }
      } : {
        hidden: {
          opacity: 0,
          y: '55%'
        },
        show: {
          opacity: 1,
          y: 0,
          transition: spring.smooth
        }
      }} className="inline-block" data-id="element-508" data-mp="1q63j6idik97w">
            {word}
            {i < words.length - 1 ? '\u00A0' : ''}
          </motion.span>
        </span>)}
    </motion.h1>;
}
function TrustLine() {
  return <ul className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[13px] text-ink-2" data-id="element-509" data-mp="25owz5da5ivwo">
      <li className="flex items-center gap-1.5" data-id="element-510" data-mp="2al58ub5kxwwg">
        <BadgeCheckIcon className="h-4 w-4 text-primary" aria-hidden="true" data-id="element-511" data-mp="1vxkaeujngluv" />
        ID, hygiene &amp; insurance checked
      </li>
      <li className="flex items-center gap-1.5" data-id="element-512" data-mp="20uj1t5bxiepd">
        <ReceiptTextIcon className="h-4 w-4 text-primary" aria-hidden="true" data-id="element-513" data-mp="2oly8196ttg5w" />
        Itemized quotes before you pay
      </li>
    </ul>;
}
export function Hero({
  treatment = 'editorial'
}: {
  treatment?: HeroTreatment;
}) {
  const {
    t
  } = useLocale();
  const ref = useRef<HTMLDivElement>(null);
  const reduce = useReducedMotion();
  const isDesktop = useIsDesktop();
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ['start start', 'end start']
  });
  const liveParallax = !reduce && isDesktop;
  const yFar = useTransform(scrollYProgress, [0, 1], [0, -40]);
  const yNear = useTransform(scrollYProgress, [0, 1], [0, 32]);
  if (treatment === 'immersive') {
    return <section ref={ref} aria-labelledby="hero-heading" className="relative isolate overflow-hidden" data-id="element-514" data-mp="2fisw8xweqcu5">
        <motion.img src={IMG.heroTable} alt="A chef plating a shared Thai feast on a linen-covered table at home" initial={reduce ? {
        opacity: 0
      } : {
        opacity: 0,
        scale: 1.04
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={reduce ? {
        duration: 0.2
      } : {
        ...spring.heavy
      }} className="absolute inset-0 h-full w-full object-cover" data-id="element-515" data-mp="2ajpqfb8ls2l4" />
        <div className="absolute inset-0 bg-ink/55" aria-hidden="true" data-id="element-516" data-mp="c2617bsm8yt6" />
        <div className="relative mx-auto flex min-h-[86vh] max-w-shell flex-col justify-end px-5 pb-14 pt-24 lg:px-8 lg:pb-20" data-id="element-517" data-mp="c40afsuiz2hf">
          <p className="mb-4 flex items-center gap-3 text-[13px] font-semibold uppercase tracking-[0.06em] text-white/80" data-id="element-518" data-mp="m0d4wsxw9kd8">
            <span className="h-px w-8 bg-white/40" aria-hidden="true" data-id="element-519" data-mp="q0nl0yfehr5t" />
            {t('home.eyebrow')}
          </p>
          <Headline text={t('home.headline')} className="max-w-[20ch] font-display text-[clamp(2.4rem,6vw,5rem)] font-semibold leading-[1.02] tracking-[-0.03em] text-white" data-id="element-520" data-mp="3pi3rkxtco6rh" />
          <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.25,
          duration: 0.3,
          ease: 'easeOut'
        }} className="mt-5 max-w-[46ch] text-lg text-white/85" data-id="element-521" data-mp="2ajeojatvdb4">
            {t('home.sub')}
          </motion.p>
          <div className="mt-9 max-w-3xl" data-id="element-522" data-mp="3aqcyb70gh4we">
            <OccasionSearch variant="bar" data-id="element-523" data-mp="2rnav856k7klh" />
          </div>
        </div>
        <div className="border-y border-hairline bg-oat py-4" data-id="element-524" data-mp="2rnkny0izo5uv">
          <div className="mx-auto max-w-shell px-5 lg:px-8" data-id="element-525" data-mp="2mp991bdu755s">
            <TrustLine data-id="element-526" data-mp="31d44gzj8bnmk" />
          </div>
        </div>
      </section>;
  }
  return <section ref={ref} aria-labelledby="hero-heading" className="relative overflow-hidden border-b border-hairline" data-id="element-527" data-mp="31exfer0vku0d">
      <div className="mx-auto grid max-w-shell items-center gap-12 px-5 py-16 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16 lg:px-8 lg:py-24" data-id="element-528" data-mp="3adis45jo1et8">
        <div data-id="element-529" data-mp="35dwfnybnw2lh">
          <p className="mb-5 flex items-center gap-3 text-[13px] font-semibold uppercase tracking-[0.06em] text-primary" data-id="element-530" data-mp="35efx7dxegoyz">
            <span className="h-px w-8 bg-primary/40" aria-hidden="true" data-id="element-531" data-mp="3k9m2tzsl2lrs" />
            {t('home.eyebrow')}
          </p>
          <Headline text={t('home.headline')} className="font-display text-[clamp(2.3rem,4.6vw,4.2rem)] font-semibold leading-[1.03] tracking-[-0.03em] text-ink" data-id="element-532" data-mp="1pxnstvmg9luu" />
          <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.28,
          duration: 0.3,
          ease: 'easeOut'
        }} className="mt-5 max-w-[48ch] text-lg leading-relaxed text-ink-2" data-id="element-533" data-mp="h22k021nzem">
            {t('home.sub')}
          </motion.p>
          <div className="mt-8" data-id="element-534" data-mp="3rv7qnw7n2cp5">
            <OccasionSearch data-id="element-535" data-mp="3ru6fzsujbs7y" />
          </div>
          <div className="mt-6" data-id="element-536" data-mp="3dvpzja6vencm">
            <TrustLine data-id="element-537" data-mp="3dvgiic3szqj2" />
          </div>
        </div>

        <div className="relative" data-id="element-538" data-mp="3dy2r8f2fkc2v">
          <motion.div style={liveParallax ? {
          y: yFar
        } : undefined} initial={reduce ? {
          opacity: 0
        } : {
          opacity: 0,
          scale: 0.97
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={reduce ? {
          duration: 0.2
        } : spring.heavy} className="overflow-hidden rounded-2xl border border-hairline will-change-transform" data-id="element-539" data-mp="3hxtew9wnuc36">
            <img src={IMG.heroTable} alt="A chef plating a shared Thai feast on a linen-covered table at home" className="aspect-[4/3] w-full object-cover" data-id="element-540" data-mp="1758qwa0qz9xm" />
          </motion.div>

          <motion.figure style={liveParallax ? {
          y: yNear
        } : undefined} initial={reduce ? {
          opacity: 0
        } : {
          opacity: 0,
          y: 24,
          scale: 0.96
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} transition={reduce ? {
          duration: 0.2
        } : {
          ...spring.bouncy,
          delay: 0.12
        }} className="absolute -bottom-8 -left-4 w-48 overflow-hidden rounded-xl border border-hairline bg-surface p-2 shadow-float will-change-transform sm:-left-8 sm:w-56" data-id="element-541" data-mp="10nyr2st1xwix">
            <img src={IMG.dishCurry} alt="Southern Thai crab curry with betel leaves and jasmine rice" className="aspect-[5/4] w-full rounded-lg object-cover" data-id="element-542" data-mp="1enq1kjkd0us6" />
            <figcaption className="px-1 pb-0.5 pt-2" data-id="element-543" data-mp="2752fdp6pcjxe">
              <span className="block font-display text-sm font-semibold text-ink" data-id="element-544" data-mp="276n8rbha5wto">Baan Ploy tasting</span>
              <span className="tnum block text-[13px] text-ink-2" data-id="element-545" data-mp="22cspg95l7jv4">from ฿2,400 / guest · 6 courses</span>
            </figcaption>
          </motion.figure>

          <motion.div initial={reduce ? {
          opacity: 0
        } : {
          opacity: 0,
          y: -16,
          scale: 0.96
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} transition={reduce ? {
          duration: 0.2
        } : {
          ...spring.bouncy,
          delay: 0.2
        }} className="absolute -right-2 top-6 rounded-xl border border-hairline bg-surface px-3.5 py-2.5 shadow-float sm:-right-6" data-id="element-546" data-mp="2eo1v9kuyftc0">
            <span className="block text-[11px] font-semibold uppercase tracking-[0.05em] text-ink-3" data-id="element-547" data-mp="2xn7jcuuzaz0t">Deposit today</span>
            <span className="tnum block font-display text-lg font-semibold text-ink" data-id="element-548" data-mp="32dw8i3j6rjws">฿7,521</span>
            <span className="block text-[12px] text-ink-2" data-id="element-549" data-mp="37dim4e2xf264">balance after service</span>
          </motion.div>
        </div>
      </div>
    </section>;
}